<template>
  <div class="page-default overflow-hidden">
    <spinner v-if="isLoading" />

    <div class="dashboard-section bg-dashboard-dark-2 overflow-auto">
      <div
        v-if="totalPages > 1"
        class="d-flex justify-content-between align-items-center px-3 py-2"
      >
        <small class="text-secondary">
          Page {{ page }} of {{ totalPages }} &middot; {{ total }} records
        </small>
        <nav aria-label="Case logs pagination (top)">
          <ul class="pagination m-0 shadow rounded overflow-hidden">
            <li class="page-item">
              <button
                type="button"
                class="btn text-white rounded-0 bg-dark"
                :disabled="page <= 1"
                @click="goToPage(page - 1)"
              >
                Prev
              </button>
            </li>
            <li
              class="page-item"
              v-for="(pg, idx) in paginationPages"
              :key="'top-' + idx"
            >
              <button
                v-if="pg !== null"
                type="button"
                class="btn text-white rounded-0"
                :class="pg === page ? 'bg-primary' : 'bg-dark'"
                @click="goToPage(pg)"
              >
                {{ pg }}
              </button>
              <span v-else class="btn text-white rounded-0 bg-dark disabled">
                &hellip;
              </span>
            </li>
            <li class="page-item">
              <button
                type="button"
                class="btn text-white rounded-0 bg-dark"
                :disabled="page >= totalPages"
                @click="goToPage(page + 1)"
              >
                Next
              </button>
            </li>
          </ul>
        </nav>
      </div>

      <div class="table-responsive">
        <table class="table table-bordered table-dark">
          <thead>
            <tr>
              <th class="fit-content">#</th>
              <th class="fit-content text-nowrap text-center">Case Number</th>
              <th class="text-center">Image</th>
              <th>Case Details</th>
              <th>Reason</th>
              <th class="fit-content text-nowrap">User</th>
              <th class="fit-content text-nowrap">Date &amp; Time</th>
              <th class="fit-content text-nowrap text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(l, index) in list">
              <td class="fit-content">
                {{ (page - 1) * pageSize + index + 1 }}
              </td>
              <td class="fit-content text-nowrap text-center">
                {{ l.data?.caseNumber }}
              </td>
              <td class="text-center" style="width: 100px">
                <div class="embed-responsive embed-responsive-1by1">
                  <img
                    v-if="l.data && l.data.media && l.data.media[0] && helper.formatMediaUrl(l.data.media[0])"
                    :src="helper.formatMediaUrl(l.data.media[0])"
                    @click="currentMedia = l.data.media[0]"
                    class="embed-responsive-item img-thumbnail clickable"
                  />
                </div>
              </td>
              <td>
                <p class="small text-secondary mb-0">
                  Timestamp: {{ l.data && l.data.timestamp ? helper.formatDate(l.data.timestamp) : "--" }}
                </p>
                <p class="small text-secondary mb-1">
                  Status: {{ l.data?.status || "--" }}
                </p>
                <p class="small mb-1">{{ l.data?.caption || "" }}</p>
              </td>
              <td>{{ l.reason || "--" }}</td>
              <td class="fit-content text-nowrap">{{ l.user?.email }}</td>
              <td class="fit-content text-nowrap">
                {{ helper.formatDate(l.createdAt) }}
              </td>
              <td class="fit-content text-center text-nowrap">
                <button class="btn btn-sm btn-primary" @click="restore(l)">
                  Restore
                </button>
              </td>
            </tr>
            <tr v-if="!isLoading && list.length === 0">
              <td colspan="8" class="text-center text-secondary py-4">
                No deleted cases to show.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        v-if="totalPages > 1"
        class="d-flex justify-content-between align-items-center px-3 py-2"
      >
        <small class="text-secondary">
          Page {{ page }} of {{ totalPages }} &middot; {{ total }} records
        </small>
        <nav aria-label="Case logs pagination">
          <ul class="pagination m-0 shadow rounded overflow-hidden">
            <li class="page-item">
              <button
                type="button"
                class="btn text-white rounded-0 bg-dark"
                :disabled="page <= 1"
                @click="goToPage(page - 1)"
              >
                Prev
              </button>
            </li>
            <li
              class="page-item"
              v-for="(pg, idx) in paginationPages"
              :key="idx"
            >
              <button
                v-if="pg !== null"
                type="button"
                class="btn text-white rounded-0"
                :class="pg === page ? 'bg-primary' : 'bg-dark'"
                @click="goToPage(pg)"
              >
                {{ pg }}
              </button>
              <span v-else class="btn text-white rounded-0 bg-dark disabled">
                &hellip;
              </span>
            </li>
            <li class="page-item">
              <button
                type="button"
                class="btn text-white rounded-0 bg-dark"
                :disabled="page >= totalPages"
                @click="goToPage(page + 1)"
              >
                Next
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>

    <media-preview
      :downloadImage="true"
      :image_data="currentMedia"
      v-if="currentMedia"
      :url="helper.formatMediaUrl(currentMedia)"
      @close="currentMedia = null"
    />
  </div>
</template>

<script>
import API from "@/api";
import Helper from "@/helper";
import Swal from "sweetalert2";
import Spinner from "@/components/Spinner";
import MediaPreview from "@/components/MediaPreview";

const FILTER = "_where[_or][0][isRestored]=false&_where[_or][1][isRestored_null]=true";

export default {
  components: {
    Spinner,
    MediaPreview,
  },
  data() {
    return {
      isLoading: false,
      list: [],
      helper: Helper,
      currentMedia: null,
      page: 1,
      pageSize: 100,
      total: 0,
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.pageSize));
    },
    paginationPages() {
      const total = this.totalPages;
      const current = this.page;
      if (total <= 7) {
        return Array.from({ length: total }, (_, i) => i + 1);
      }
      const pages = new Set([1, total, current, current - 1, current + 1]);
      if (current <= 3) {
        pages.add(2);
        pages.add(3);
      }
      if (current >= total - 2) {
        pages.add(total - 1);
        pages.add(total - 2);
      }
      const sorted = [...pages]
        .filter((p) => p >= 1 && p <= total)
        .sort((a, b) => a - b);
      const result = [];
      let prev = 0;
      for (const p of sorted) {
        if (p - prev > 1) result.push(null);
        result.push(p);
        prev = p;
      }
      return result;
    },
  },
  methods: {
    async getCount() {
      try {
        const result = await API.get(`deleted-cases/count?${FILTER}`);
        this.total = typeof result.data === "number" ? result.data : 0;
      } catch (err) {
        console.error(err);
        this.total = 0;
      }
    },
    async getLogs() {
      this.isLoading = true;

      const start = (this.page - 1) * this.pageSize;
      try {
        const result = await API.get(
          `deleted-cases?${FILTER}&_sort=createdAt:DESC&_limit=${this.pageSize}&_start=${start}`
        );
        this.list = result.data;
      } catch (err) {
        console.error(err);
        Swal.fire(
          "Failed to load page",
          "The page took too long or the server returned an error. Please try again.",
          "error"
        );
      } finally {
        this.isLoading = false;
      }
    },
    async goToPage(n) {
      if (n < 1 || n > this.totalPages || n === this.page) return;
      this.page = n;
      await this.getLogs();
    },
    restore(list) {
      if (!list.data) {
        Swal.fire(
          "Cannot restore",
          "This record has no case data attached and cannot be restored.",
          "error"
        );
        return;
      }
      Swal.fire({
        title: "Restore this case?",
        icon: "question",
        showCancelButton: true,
        reverseButtons: true,
        confirmButtonText: "Restore",
      }).then(async (result) => {
        if (!result.isConfirmed) return;

        this.isLoading = true;
        try {
          await API.post("cases", list.data);
          await API.put(`deleted-cases/${list.id}`, { isRestored: true });
          await this.getCount();
          if (this.page > this.totalPages) {
            this.page = this.totalPages;
          }
          await this.getLogs();
          Swal.fire("Case restored", "", "success");
        } catch (err) {
          console.error(err);
          Swal.fire("Restore failed", "Please try again.", "error");
        } finally {
          this.isLoading = false;
        }
      });
    },
  },
  async mounted() {
    await this.getCount();
    await this.getLogs();
  },
};
</script>
